/**
 * `/collection`: the switches and cost levers for sampling related posts' full
 * metrics through `post/stats`. Whether and how often likers are read now
 * rides each media kind's own `likers_enabled` (the profile/tracking cadence
 * cards); this page keeps only the provider's own per-post cap.
 * Durations are stored in seconds and edited through `DurationInput`, in the unit an
 * operator thinks in; the floors match `api`'s.
 */

import { useState, type ReactNode } from 'react';

import { DurationInput, durationInRange } from '../../chrome/form';
import { Page, PageHeader } from '../../chrome/page';
import { failureSentence, LoadingState, QueryStates } from '../../chrome/state';
import { ApiFailureError, type ApiFailure } from '../../lib/http/errors';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Input } from '../../ui/input';
import { Switch } from '../../ui/switch';
import { usePostStatsSettings, useSavePostStatsSettings } from '../api/queries';
import type { PostStatsSettings } from '../api/schemas';
import { opsTitles } from '../shell/titles';
import { t } from './messages';

type Field = 'supporterFactor' | 'likersMax';

const MIN_INTERVAL_FLOOR_SECONDS = 60;
const SUPPORTER_CAP_FLOOR_SECONDS = 3600;

/** Matches the API's ceiling: one likers call has returned up to 2,140 and never paginates. */
const LIKERS_MAX = 5000;

function Row(props: { name: string; hint: string; children: ReactNode }): ReactNode {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 py-3">
      <div className="flex max-w-prose flex-col gap-0.5">
        <span className="font-medium">{props.name}</span>
        <span className="text-caption text-muted-foreground">{props.hint}</span>
      </div>
      {props.children}
    </div>
  );
}

function SettingsForm(props: { settings: PostStatsSettings }): ReactNode {
  const { settings } = props;
  const save = useSavePostStatsSettings();
  const [enabled, setEnabled] = useState(settings.enabled);
  const [values, setValues] = useState<Record<Field, string>>({
    supporterFactor: String(settings.supporter_factor),
    likersMax: String(settings.likers_max_per_post),
  });
  const [minInterval, setMinInterval] = useState<number | null>(settings.min_interval_seconds);
  const [supporterCap, setSupporterCap] = useState<number | null>(settings.supporter_cap_seconds);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  const parsed = {
    supporterFactor: Number(values.supporterFactor),
    likersMax: Number(values.likersMax),
  };
  const valid =
    Object.values(parsed).every((n) => Number.isInteger(n) && n >= 1) &&
    parsed.likersMax <= LIKERS_MAX &&
    durationInRange(minInterval, MIN_INTERVAL_FLOOR_SECONDS) &&
    durationInRange(supporterCap, SUPPORTER_CAP_FLOOR_SECONDS);

  function handleSave(): void {
    if (minInterval === null || supporterCap === null) return;
    setFailure(null);
    save.mutate(
      {
        enabled,
        min_interval_seconds: minInterval,
        supporter_factor: parsed.supporterFactor,
        supporter_cap_seconds: supporterCap,
        likers_max_per_post: parsed.likersMax,
      },
      {
        onError: (error) =>
          setFailure(
            error instanceof ApiFailureError ? error.failure : { kind: 'server', status: 0 },
          ),
      },
    );
  }

  function numberField(field: Field): ReactNode {
    return (
      <Input
        type="number"
        min={1}
        max={field === 'likersMax' ? LIKERS_MAX : undefined}
        step={1}
        aria-label={t(`${field}.name`)}
        aria-invalid={
          !Number.isInteger(parsed[field]) ||
          parsed[field] < 1 ||
          (field === 'likersMax' && parsed[field] > LIKERS_MAX)
        }
        value={values[field]}
        onChange={(event) => setValues({ ...values, [field]: event.target.value })}
        className="w-24"
      />
    );
  }

  return (
    <Card>
      <CardContent className="divide-border flex flex-col divide-y">
        <Row name={t('enabled.name')} hint={t('enabled.hint')}>
          <Switch aria-label={t('enabled.name')} checked={enabled} onCheckedChange={setEnabled} />
        </Row>
        <Row name={t('minInterval.name')} hint={t('minInterval.hint')}>
          <div className="w-64">
            <DurationInput
              aria-label={t('minInterval.name')}
              seconds={minInterval}
              onChange={setMinInterval}
              minSeconds={MIN_INTERVAL_FLOOR_SECONDS}
              units={['minutes', 'hours', 'days']}
            />
          </div>
        </Row>
        <Row name={t('supporterFactor.name')} hint={t('supporterFactor.hint')}>
          {numberField('supporterFactor')}
        </Row>
        <Row name={t('supporterCap.name')} hint={t('supporterCap.hint')}>
          <div className="w-64">
            <DurationInput
              aria-label={t('supporterCap.name')}
              seconds={supporterCap}
              onChange={setSupporterCap}
              minSeconds={SUPPORTER_CAP_FLOOR_SECONDS}
              units={['hours', 'days', 'weeks']}
            />
          </div>
        </Row>
        <Row name={t('likersMax.name')} hint={t('likersMax.hint')}>
          {numberField('likersMax')}
        </Row>
        <div className="flex items-center justify-end gap-3 pt-3">
          {(failure !== null || !valid) && (
            <p role="alert" className="text-caption text-destructive">
              {failure === null || failure.kind === 'invalid'
                ? t('invalid')
                : failureSentence(failure)}
            </p>
          )}
          {save.isSuccess && failure === null && (
            <p role="status" className="text-caption text-muted-foreground">
              {t('saved')}
            </p>
          )}
          <Button type="button" disabled={!valid || save.isPending} onClick={handleSave}>
            {t('save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function Collection(): ReactNode {
  const query = usePostStatsSettings();
  return (
    <Page>
      <PageHeader title={opsTitles.t('collection')} />
      <QueryStates<PostStatsSettings> query={query} loading={<LoadingState label={t('title')} />}>
        {(settings) => <SettingsForm key={JSON.stringify(settings)} settings={settings} />}
      </QueryStates>
    </Page>
  );
}
